import React from 'react';
import { getMessage } from '@tcc/i18n';
import { Icon } from '@tcc/crate';
import cx from 'classnames';
import { ApplicationProduct } from '../../../types/Application';
import { SortField } from '../../../types/Sort';
import { getApplicationLocaleGroup, getApplicationAmountLocaleGroup } from '../../../utils/newProcessUnits';
import { localizeUnits } from '../../../utils/processUnits';
import styles from './ApplicationsPanel.css';

interface ProductTableProps {
  products: ApplicationProduct[];
  sortField: SortField;
  sortProducts: (products: ApplicationProduct[]) => ApplicationProduct[];
  handleSort: (field: SortField) => void;
  getSortIcon: (field: SortField) => string;
}

const ProductTable = ({
  products,
  sortField,
  sortProducts,
  handleSort,
  getSortIcon,
}: ProductTableProps) => {
  if (products.length === 0) {
    return null;
  }

  return (
    <div className={styles.applicationsPanelProductBreakdown}>
      <table className={styles.applicationsPanelProductTable}>
        <thead>
          <tr>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.NAME)}
                aria-label="Product"
              >
                {getMessage('application.details.product')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.NAME })}
                  glyph={getSortIcon(SortField.NAME)}
                />
              </button>
            </th>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.RATE)}
                aria-label="Rate"
              >
                {getMessage('application.details.rate')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.RATE })}
                  glyph={getSortIcon(SortField.RATE)}
                />
              </button>
            </th>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.APPLIED)}
                aria-label="Applied"
              >
                {getMessage('application.details.applied')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.APPLIED })}
                  glyph={getSortIcon(SortField.APPLIED)}
                />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortProducts(products).map((product) => {
            const rateLocaleGroup = getApplicationLocaleGroup(product.rate.u);
            const rateUnits = localizeUnits(product.rate.q, {
              key: product.rate.u,
              localeGroup: rateLocaleGroup,
            });
            const appliedLocaleGroup = getApplicationAmountLocaleGroup(product.applied.u);
            const appliedUnits = localizeUnits(product.applied.q, {
              key: product.applied.u,
              localeGroup: appliedLocaleGroup,
            });
            
            return (
              <tr key={product.name}>
                <td className={styles.applicationsPanelProductTableCell}>
                  {product.name}
                </td>
                <td className={styles.applicationsPanelProductTableCell}>
                  <span className={styles.applicationsPanelValueText}>{rateUnits.q}</span>
                  <br />
                  <span className={styles.applicationsPanelUnitText}>{rateUnits.u}</span>
                </td>
                <td className={styles.applicationsPanelProductTableCell}>
                  <span className={styles.applicationsPanelValueText}>{appliedUnits.q}</span>
                  <br />
                  <span className={styles.applicationsPanelUnitText}>{appliedUnits.u}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
export type { ProductTableProps };
